import Swal from 'sweetalert2';

/**
 * SweetAlert2 工具函數
 * 統一管理所有提示訊息
 */

/**
 * 成功提示
 * @param {string} title - 標題
 * @param {string} text - 內容（可選）
 * @param {number} timer - 自動關閉時間（毫秒，可選）
 */
export const showSuccess = (title, text = '', timer = 2000) => {
  return Swal.fire({
    icon: 'success',
    title: title,
    text: text,
    timer: timer,
    timerProgressBar: true,
    showConfirmButton: timer === 0,
    confirmButtonText: '確定',
    confirmButtonColor: '#667eea',
  });
};

/**
 * 錯誤提示
 * @param {string} title - 標題
 * @param {string} text - 內容（可選）
 */
export const showError = (title, text = '') => {
  return Swal.fire({
    icon: 'error',
    title: title,
    text: text,
    confirmButtonText: '確定',
    confirmButtonColor: '#dc3545',
  });
};

/**
 * 警告提示
 * @param {string} title - 標題
 * @param {string} text} - 內容（可選）
 */
export const showWarning = (title, text = '') => {
  return Swal.fire({
    icon: 'warning',
    title: title,
    text: text,
    confirmButtonText: '確定',
    confirmButtonColor: '#ffc107',
  });
};

/**
 * 資訊提示
 * @param {string} title - 標題
 * @param {string} text - 內容（可選）
 */
export const showInfo = (title, text = '') => {
  return Swal.fire({
    icon: 'info',
    title: title,
    text: text,
    confirmButtonText: '確定',
    confirmButtonColor: '#17a2b8',
  });
};

/**
 * 確認對話框
 * @param {string} title - 標題
 * @param {string} text - 內容（可選）
 * @param {string} confirmText - 確認按鈕文字（可選，預設「確定」）
 * @param {string} cancelText - 取消按鈕文字（可選，預設「取消」）
 * @param {string} icon - 圖標類型（可選，預設「warning」）
 * @returns {Promise} 返回 Promise，resolve 為 true 表示確認，false 表示取消
 */
export const showConfirm = (
  title,
  text = '',
  confirmText = '確定',
  cancelText = '取消',
  icon = 'warning'
) => {
  return Swal.fire({
    icon: icon,
    title: title,
    text: text,
    showCancelButton: true,
    confirmButtonText: confirmText,
    cancelButtonText: cancelText,
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    reverseButtons: true, // 確認按鈕在右側
    allowOutsideClick: false, // 避免在 click handler 內開啟時被同一 click 立即關閉
  }).then((result) => {
    return result.isConfirmed;
  });
};

/**
 * 從 click handler 開啟確認對話框（延遲至 click 事件結束，避免 SweetAlert2 立刻被關閉）
 */
export const showConfirmDeferred = (
  title,
  text = '',
  confirmText = '確定',
  cancelText = '取消',
  icon = 'warning'
) => {
  return new Promise((resolve) => {
    window.setTimeout(() => {
      showConfirm(title, text, confirmText, cancelText, icon).then(resolve);
    }, 100);
  });
};

/**
 * 刪除確認對話框（專用）
 * @param {string} itemName - 項目名稱（例如：帳號名稱）
 * @param {string|Array} customMessage - 自定義訊息（可選），可以是字串或項目陣列
 * @returns {Promise} 返回 Promise，resolve 為 true 表示確認刪除
 */
export const showDeleteConfirm = (itemName, customMessage = null) => {
  let message;
  
  if (customMessage) {
    if (Array.isArray(customMessage)) {
      // 如果是陣列，轉換為 HTML 列表
      const itemsList = customMessage.map(item => `<li style="margin: 6px 0;">${item}</li>`).join('');
      message = `確定要刪除「${itemName}」嗎？<br><br><div style="text-align: center; margin: 12px 0;"><strong>將同時刪除以下項目：</strong></div><ul style="text-align: left; margin: 12px auto; padding-left: 24px; display: inline-block; min-width: 200px;">${itemsList}</ul><div style="text-align: center; margin-top: 12px;"><strong>此操作無法復原！</strong></div>`;
    } else {
      message = customMessage;
    }
  } else {
    message = `確定要刪除「${itemName}」嗎？此操作無法復原！`;
  }
  
  return Swal.fire({
    icon: 'warning',
    title: '確定要刪除嗎？',
    html: message,
    showCancelButton: true,
    confirmButtonText: '刪除',
    cancelButtonText: '取消',
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    reverseButtons: true,
    allowOutsideClick: false,
  }).then((result) => {
    return result.isConfirmed;
  });
};

export const showDeleteConfirmDeferred = (itemName, customMessage = null) => {
  return new Promise((resolve) => {
    window.setTimeout(() => {
      showDeleteConfirm(itemName, customMessage).then(resolve);
    }, 100);
  });
};

/**
 * 從 click handler 開啟自訂 SweetAlert2 對話框
 */
export const fireSwalDeferred = (options) => {
  return new Promise((resolve) => {
    window.setTimeout(() => {
      Swal.fire({
        allowOutsideClick: false,
        ...options,
      }).then(resolve);
    }, 100);
  });
};

/**
 * 載入中提示
 * @param {string} title - 標題（可選）
 */
export const showLoading = (title = '處理中...') => {
  Swal.fire({
    title: title,
    allowOutsideClick: false,
    allowEscapeKey: false,
    didOpen: () => {
      Swal.showLoading();
    },
  });
};

export const chooseInvoiceParty = ({ versions = [], defaultVersion } = {}) => {
  const numbers = [...new Set(
    (versions.length ? versions : [{ versionNo: defaultVersion || 1 }])
      .map((version) => Number(version.versionNo))
      .filter((versionNo) => Number.isInteger(versionNo) && versionNo > 0)
  )].sort((a, b) => b - a);
  const selected = numbers.includes(Number(defaultVersion)) ? Number(defaultVersion) : numbers[0];
  const options = numbers.map((versionNo) => (
    `<option value="${versionNo}"${versionNo === selected ? ' selected' : ''}>第 ${versionNo} 版${versionNo === numbers[0] ? '（最後一版）' : ''}</option>`
  )).join('');
  return Swal.fire({
    icon: 'question',
    title: '設為已成交',
    html: `
      <div style="text-align:left">
        <p style="margin:0 0 12px">請選擇要用哪一版成交，以及發票開給誰。預設為最後一版。成交後不能再修改。</p>
        <label for="quote-won-version" style="display:block;margin:0 0 6px;font-weight:600">成交版本</label>
        <select id="quote-won-version" class="swal2-select" style="display:flex;width:100%;margin:0 0 16px">
          ${options}
        </select>
        <p style="margin:0 0 6px;font-weight:600">發票對象</p>
        <label style="display:block;margin:6px 0">
          <input type="radio" name="quote-won-party" value="customer" checked> 對客戶報價
        </label>
        <label style="display:block;margin:6px 0">
          <input type="radio" name="quote-won-party" value="dealer"> 對經銷商報價
        </label>
      </div>
    `,
    focusConfirm: false,
    showCancelButton: true,
    confirmButtonText: '設為已成交',
    cancelButtonText: '取消',
    confirmButtonColor: '#dc3545',
    cancelButtonColor: '#6c757d',
    preConfirm: () => {
      const versionNo = Number(document.getElementById('quote-won-version')?.value);
      const invoiceParty = document.querySelector('input[name="quote-won-party"]:checked')?.value;
      if (!Number.isInteger(versionNo) || versionNo < 1) {
        Swal.showValidationMessage('請選擇成交版本');
        return false;
      }
      if (!['customer', 'dealer'].includes(invoiceParty)) {
        Swal.showValidationMessage('請選擇報價對象');
        return false;
      }
      return { versionNo, invoiceParty };
    }
  }).then((result) => (result.isConfirmed ? result.value : null));
};

/**
 * 關閉當前提示
 */
export const closeSwal = () => {
  Swal.close();
};

export default {
  showSuccess,
  showError,
  showWarning,
  showInfo,
  showConfirm,
  showConfirmDeferred,
  showDeleteConfirm,
  chooseInvoiceParty,
  showDeleteConfirmDeferred,
  fireSwalDeferred,
  showLoading,
  closeSwal,
};
